// Story 1.17, REPOINTED BY STORY 8.5 — TanStack Query hook for
// GET /api/v1/creators/me/payout/account.
//
// It reads the CREATOR's settlement destination: where Greenhold pays their
// revenue share. Approved creators only; there is no user-facing equivalent and
// there is not going to be one (owner ruling 2026-08-20, NP+C — a purchased
// balance is closed-loop and non-payable, so a user has nothing to register a
// bank account FOR).
//
// It used to drive the bank-setup-vs-cashout branch on
// <BalanceDisplay variant="full">. That branch, and the props behind it, were
// removed by Story 8.3 and are not coming back.

import { useQuery } from "@tanstack/react-query";

import { attachRequestIdToScope } from "../../lib/api-client";

export type PayoutAccountVerificationStatus =
  | "pending_verification"
  | "verified";

export type PayoutAccount = {
  status: PayoutAccountVerificationStatus;
  iban_last4: string;
  holder_name: string;
};

/** Null = no settlement destination registered yet. */
export type PayoutAccountState = PayoutAccount | null;

export const PAYMENT_ACCOUNT_QUERY_KEY = ["payment-account-status"] as const;

async function fetchPayoutAccount(): Promise<PayoutAccountState> {
  const response = await fetch("/api/v1/creators/me/payout/account", {
    credentials: "include",
  });
  attachRequestIdToScope(response);
  // 404-style empty state: nothing registered yet.
  if (response.status === 404) return null;
  if (!response.ok) {
    throw new Error(`payment account fetch failed: ${response.status}`);
  }
  const body = (await response.json()) as Partial<PayoutAccount> | null;
  // Tolerate a 200-with-empty-body empty state as well as a hard 404.
  if (!body?.status || !body.iban_last4) return null;
  return body as PayoutAccount;
}

export function usePaymentAccountStatus(options?: { refetchIntervalMs?: number }) {
  return useQuery({
    queryKey: PAYMENT_ACCOUNT_QUERY_KEY,
    queryFn: fetchPayoutAccount,
    staleTime: 30_000,
    gcTime: 5 * 60_000,
    refetchInterval: options?.refetchIntervalMs,
    retry: false,
  });
}
